<template>
  <div class="min-h-screen w-full">
    <NuxtLayout>
      <article>
        <div class="container-wrap py-12">
          <div class="desktop:flex desktop:items-center">
            <div
              class="mb-5 w-full desktop:mb-0 desktop:w-[calc(100%-580px)] desktop:pr-5"
            >
              <div class="min-h-[160px]">
                <h1 class="mb-4 text-3xl font-bold">
                  {{ errorTitle }}
                  <span class="text-blue-400">{{ error.statusCode }}</span>
                </h1>
                <p class="mb-2 text-base">{{ errorContent }}</p>
              </div>
              <div class="flex flex-wrap justify-between tablet:justify-start">
                <TheButton
                  :to="`/${locale}/marketplace`"
                  :title="t('back-home-button')"
                  class="mb-3 w-[calc((100%-0.75rem)/2)] tablet:mr-5 tablet:w-[150px] desktop:w-[180px]"
                >
                  <span class="text-sm">{{ t('back-home-button') }}</span>
                </TheButton>
                <TheButton
                  :to="`/${locale}/marketplace/help/contact-us`"
                  :title="t('contact-us-button')"
                  type="outline"
                  class="mb-3 w-[calc((100%-0.75rem)/2)] tablet:w-[150px] desktop:w-[180px]"
                >
                  <span class="text-sm">{{ t('contact-us-button') }}</span>
                </TheButton>
              </div>
            </div>
            <figure class="relative w-full desktop:w-[580px]">
              <img
                src="@/assets/images/error-page-banner.gif"
                alt="Error Page Banner"
                loading="lazy"
                class="pointer-events-none block w-full"
              />
            </figure>
          </div>
        </div>
      </article>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
  const props = defineProps({
    error: {
      type: Object,
      required: true,
    },
  });

  const { t, locale } = useI18n({ useScope: 'local' });
  const route = useRoute();
  const menuStore = useMenuStore();
  const profileStore = useProfileStore();
  const searchMenuStore = useSearchMenuStore();
  const { scripts } = useMetaScript();
  const { canonical, alternate, preconnect, htmlLocaleName } = useMetaLink();
  const { handleScrollTo } = useScrollTo({ behavior: 'auto' });

  // 產品在目標地區未上架時，404 頁需顯示專屬文案
  const isProductRegionUnavailable = computed(
    (): boolean =>
      (props.error as { data?: { type?: string } })?.data?.type ===
      'product-region-unavailable'
  );

  const errorTitle = computed((): string => {
    switch (props.error.statusCode) {
      case 400:
        return t('400-title');
      case 404:
        return isProductRegionUnavailable.value
          ? t('404-product-title')
          : t('404-title');
      default:
        return t('500-title');
    }
  });

  const errorContent = computed((): string => {
    switch (props.error.statusCode) {
      case 400:
        return t('400-content');
      case 404:
        return isProductRegionUnavailable.value
          ? t('404-product-content')
          : t('404-content');
      default:
        return t('500-content');
    }
  });

  // * Watching Route Change

  watch(
    () => route.path,
    () => {
      // Scroll To Top
      handleScrollTo();
      // Close Menu
      menuStore.HANDLE_CLOSE_MENU();
      searchMenuStore.HANDLE_CLOSE_MENU();
    }
  );

  onMounted(() => {
    profileStore.GET_PROFILE();
    menuStore.HANDLE_CLOSE_MENU();
    searchMenuStore.HANDLE_CLOSE_MENU();
  });

  onBeforeUnmount(() => {
    clearError();
  });

  onBeforeRouteLeave(() => {
    clearError();
  });

  useHead({
    htmlAttrs: {
      lang: () => htmlLocaleName.value,
    },
    link: () => [...preconnect.value, ...canonical.value, ...alternate.value],
    script: scripts.value,
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "400-title": "The page isn't working at the moment:",
    "400-content": "Please reload the page, we are trying to solve the problem.",
    "404-title": "Page is not found:",
    "404-content": "The page you're looking for doesn't exist. It may have been removed, renamed, or is temporarily unavailable.",
    "404-product-title": "Sorry, this page isn't available:",
    "404-product-content": "This page may be unavailable due to an incorrect URL or the product not being offered in your region. Please check the URL, use search, or visit the homepage to browse the latest content.",
    "500-title": "Oops! Something went wrong:",
    "500-content": "Please reload the page, we are trying to solve the problem.",
    "back-home-button": "Back Home",
    "contact-us-button": "Contact Us"
  },
  "en-jp": {
    "400-title": "The page isn't working at the moment:",
    "400-content": "Please reload the page, we are trying to solve the problem.",
    "404-title": "Page is not found:",
    "404-content": "The page you're looking for doesn't exist. It may have been removed, renamed, or is temporarily unavailable.",
    "404-product-title": "Sorry, this page isn't available:",
    "404-product-content": "This page may be unavailable due to an incorrect URL or the product not being offered in your region. Please check the URL, use search, or visit the homepage to browse the latest content.",
    "500-title": "Oops! Something went wrong:",
    "500-content": "Please reload the page, we are trying to solve the problem.",
    "back-home-button": "Back Home",
    "contact-us-button": "Contact Us"
  },
  "en-tw": {
    "400-title": "The page isn't working at the moment:",
    "400-content": "Please reload the page, we are trying to solve the problem.",
    "404-title": "Page is not found:",
    "404-content": "The page you're looking for doesn't exist. It may have been removed, renamed, or is temporarily unavailable.",
    "404-product-title": "Sorry, this page isn't available:",
    "404-product-content": "This page may be unavailable due to an incorrect URL or the product not being offered in your region. Please check the URL, use search, or visit the homepage to browse the latest content.",
    "500-title": "Oops! Something went wrong:",
    "500-content": "Please reload the page, we are trying to solve the problem.",
    "back-home-button": "Back Home",
    "contact-us-button": "Contact Us"
  },
  "en-kr": {
    "400-title": "The page isn't working at the moment:",
    "400-content": "Please reload the page, we are trying to solve the problem.",
    "404-title": "Page is not found:",
    "404-content": "The page you're looking for doesn't exist. It may have been removed, renamed, or is temporarily unavailable.",
    "404-product-title": "Sorry, this page isn't available:",
    "404-product-content": "This page may be unavailable due to an incorrect URL or the product not being offered in your region. Please check the URL, use search, or visit the homepage to browse the latest content.",
    "500-title": "Oops! Something went wrong:",
    "500-content": "Please reload the page, we are trying to solve the problem.",
    "back-home-button": "Back Home",
    "contact-us-button": "Contact Us"
  },
  "en-americas": {
    "400-title": "The page isn't working at the moment:",
    "400-content": "Please reload the page, we are trying to solve the problem.",
    "404-title": "Page is not found:",
    "404-content": "The page you're looking for doesn't exist. It may have been removed, renamed, or is temporarily unavailable.",
    "404-product-title": "Sorry, this page isn't available:",
    "404-product-content": "This page may be unavailable due to an incorrect URL or the product not being offered in your region. Please check the URL, use search, or visit the homepage to browse the latest content.",
    "500-title": "Oops! Something went wrong:",
    "500-content": "Please reload the page, we are trying to solve the problem.",
    "back-home-button": "Back Home",
    "contact-us-button": "Contact Us"
  },
  "en-asia-pacific": {
    "400-title": "The page isn't working at the moment:",
    "400-content": "Please reload the page, we are trying to solve the problem.",
    "404-title": "Page is not found:",
    "404-content": "The page you're looking for doesn't exist. It may have been removed, renamed, or is temporarily unavailable.",
    "404-product-title": "Sorry, this page isn't available:",
    "404-product-content": "This page may be unavailable due to an incorrect URL or the product not being offered in your region. Please check the URL, use search, or visit the homepage to browse the latest content.",
    "500-title": "Oops! Something went wrong:",
    "500-content": "Please reload the page, we are trying to solve the problem.",
    "back-home-button": "Back Home",
    "contact-us-button": "Contact Us"
  },
  "en-emea": {
    "400-title": "The page isn't working at the moment:",
    "400-content": "Please reload the page, we are trying to solve the problem.",
    "404-title": "Page is not found:",
    "404-content": "The page you're looking for doesn't exist. It may have been removed, renamed, or is temporarily unavailable.",
    "404-product-title": "Sorry, this page isn't available:",
    "404-product-content": "This page may be unavailable due to an incorrect URL or the product not being offered in your region. Please check the URL, use search, or visit the homepage to browse the latest content.",
    "500-title": "Oops! Something went wrong:",
    "500-content": "Please reload the page, we are trying to solve the problem.",
    "back-home-button": "Back Home",
    "contact-us-button": "Contact Us"
  },
  "ja-jp": {
    "400-title": "このページは現在機能していません:",
    "400-content": "ページをリロードしてください。",
    "404-title": "ページが見つかりません:",
    "404-content": "お探しのページは存在しない、移動・削除された、名前が変更された、または一時的に利用できません。",
    "404-product-title": "ページが見つかりません：",
    "404-product-content": "URLの誤り、または対象地域外のため表示できません。URLをご確認のうえ、検索またはトップページからお進みください。",
    "500-title": "おっと！何かおかしい:",
    "500-content": "ページをリロードしてください。",
    "back-home-button": "ホームに戻る",
    "contact-us-button": "お問い合わせ"
  },
  "zh-cn": {
    "400-title": "该页面暂时无法使用:",
    "400-content": "请重新加载页面，我们正在努力解决问题。",
    "404-title": "页面未找到:",
    "404-content": "您正在寻找的页面不存在，可能已被移除、更名或暂时无法使用。",
    "404-product-title": "抱歉，此页面无法访问：",
    "404-product-content": "可能因网址错误，或该产品未在您所在的地区提供，导致此页面无法显示。请检查网址、使用搜索功能，或前往首页浏览最新内容。",
    "500-title": "哎呀！出错了:",
    "500-content": "请重新加载页面，我们正在努力解决问题。",
    "back-home-button": "返回首页",
    "contact-us-button": "联系我们"
  },
  "zh-tw": {
    "400-title": "該網頁無法正常運作:",
    "400-content": "請您重新加載頁面，我們正在努力解決問題。",
    "404-title": "頁面未找到:",
    "404-content": "您正在尋找的頁面不存在，可能已被移除、更名或暫時無法使用。",
    "404-product-title": "抱歉，此頁面無法使用：",
    "404-product-content": "可能因網址錯誤，或該產品未在您所在的地區提供，導致此頁面無法顯示。請檢查網址、使用搜尋功能，或前往首頁瀏覽最新內容。",
    "500-title": "哎呀！發生了一些問題:",
    "500-content": "請您重新加載頁面，我們正在努力解決問題。",
    "back-home-button": "返回首頁",
    "contact-us-button": "聯繫我們"
  }
}
</i18n>
